// TypeScript интерфейсы для Generator V2

type JsonObject = Record<string, unknown>;
type TemplateMetadata = {
  theme?: string;
  isSmokeTemplate?: boolean;
  isTestTemplate?: boolean;
} & JsonObject;

export interface ComponentVariant {
  id: number;
  component_name: string;
  variant_code: string;
  file_path: string;
  css_file_path: string;
  position_min: number;
  position_max: number;
  max_usage: number;
  template_area_type: string;
  component_category: string;
  complexity_level: string;
  content_type: string;
  is_active: boolean;
}

export interface ComponentsByType {
  [componentType: string]: ComponentVariant[];
}

export interface ComponentsResponse {
  success: boolean;
  data: {
    components: ComponentVariant[];
    componentsByType: ComponentsByType;
    total: number;
  };
}

export interface HtmlTemplateContainer {
  container_id: number;
  container_name: string;
  container_type: string;
  column_id: number;
  sort_order: number;
  components: ContainerComponent[];
}

export interface ContainerComponent {
  rule_id: number;
  component_variant_id: number;
  component_name: string;
  rule_component_name?: string;
  variant_code: string;
  file_path: string;
  css_file_path: string;
  template_area_type: string;
  component_category: string;
  selection_mode?: ComponentSelectionMode;
  is_required: boolean;
  is_allowed: boolean;
  min_instances: number;
  max_instances: number;
  sort_order: number;
}

export interface HtmlTemplate {
  id: number;
  name: string;
  description: string;
  template_type: string;
  layout_structure: unknown;
  is_active: boolean;
  is_published?: boolean;
  created_at: string;
  container_count: number;
  metadata?: TemplateMetadata;
  containers?: HtmlTemplateContainer[];
  is_multipage?: boolean; // #Gen-Multi: Флаг многостраничного шаблона
}

// #Gen-Multi: Тип сайта для фильтрации шаблонов
export type SiteType = 'landing' | 'multipage';

export interface ComponentVariantOption {
  id: number;
  variantCode: string;
  fullName: string;
}

export type ComponentSelectionMode = 'specific' | 'category';

export interface RecommendedComponent {
  componentId: number;
  componentName?: string; // 🆕 #F-116: Имя типа компонента
  variantCode: string;
  containerId?: number; // Опционально, так как может быть несколько контейнеров
  containerName?: string; // Опционально
  containerIds?: number[]; // 🆕 Список контейнеров
  containerNames?: string[]; // 🆕 Список имен контейнеров
  containerTypes?: string[]; // 🆕 Типы контейнеров (header, footer, column и т.д.)
  columnIds?: string[]; // 🆕 ID колонок для определения позиции
  isRequired: boolean;
  minInstances: number;
  maxInstances: number;
  sortOrder: number;
  totalInstances?: number; // 🆕 Общее количество экземпляров
  availableVariants?: ComponentVariantOption[]; // 🆕 Доступные варианты для выбора
  ruleSelectionMode?: ComponentSelectionMode | null; // 🆕 Режим выбора из правила
  ruleComponentName?: string | null; // 🆕 Точное имя компонента из правила
}

export interface TemplateComponentsResponse {
  success: boolean;
  data: {
    containers: HtmlTemplateContainer[];
    recommendedComponents: RecommendedComponent[];
    totalContainers: number;
    totalComponents: number;
  };
}

export interface CssTheme {
  id: number;
  name: string;
  cssClass: string;
  description: string;
  isDark?: boolean;
  previewColors?: {
    primary: string;
    secondary: string;
    background: string;
  };
}

export interface ContentTheme {
  key: string;
  label: string;
  description: string;
}

export interface TemplateAccessInfo {
  templatePublished?: boolean;
  hasUnpublishedContainers?: boolean;
  hasRequiredUnpublishedComponents?: boolean;
}

export type WarningSeverity = 'info' | 'warning' | 'error';

export interface TemplateWarning {
  type: string;
  message: string;
  severity?: WarningSeverity;
  containerId?: number;
  containerName?: string;
  ruleId?: number;
}

export interface TemplateAvailability {
  hasUnpublishedContainers?: boolean;
  hasMenuReminders?: boolean;
  hasUnpublishedComponents?: boolean;
  totalContainers?: number;
  totalComponents?: number;
}

export interface ThemesResponse {
  success: boolean;
  data: CssTheme[];
}

export interface ComponentSelection {
  componentId: number;
  variantCode: string | null; // null означает случайный выбор
  containerId?: number;
  containerName?: string;
  instances?: number;
  componentName?: string; // 🆕 Имя типа компонента для сопоставления
  forceInclude?: boolean; // Принудительное включение необязательного компонента
  forceExclude?: boolean; // 🆕 Принудительное выключение необязательного компонента
  randomInclude?: boolean; // 🆕 Случайное включение (50%)
  randomVariant?: boolean; // Случайный выбор варианта
  customSettings?: JsonObject;
}

export type ThemeMode = 'template' | 'list' | 'custom';

export interface ThemeResolutionInfo {
  theme: string;
  source?: 'template' | 'list' | 'custom' | 'fallback';
  reason?: string;
  templateTheme?: string | null;
  allowedThemes?: string[];
  matchedKeywords?: string[];
  descriptionUsed?: string;
  mode?: ThemeMode;
}

export interface GenerationRequestV2 {
  templateType: 'single' | 'multi-page';
  htmlTemplate: number; // ID из html_templates
  selectedComponents?: ComponentSelection[];
  theme?: string; // CSS тема из themes.css
  themeMode?: ThemeMode;
  themeDescription?: string;
  siteContentTheme?: string;
  siteDescriptionOverride?: string;
  platform: string; // #Core-Legal: Расширен до любой платформы из конфига
  geo: string; // #Core-Legal: Расширен до любой юрисдикции из конфига
  contentLanguage: string;
  legalDocsLanguage?: string; // #Core-Legal: '' = язык сайта, 'en' = английский
  // #Core-Legal: Данные компании для юридических документов
  companyName?: string;
  companyEmail?: string;
  companyAddress?: string;
  companyDomain?: string;
  // Расширенный режим
  advancedMode?: boolean;
  communicationStyle?: 'professional' | 'friendly' | 'aggressive' | 'minimal' | 'creative';
  specialRequirements?: string;
  debugMode?: boolean;
  // 🆕 #F-Background: Настройки глобального фона
  backgroundOverride?: {
    enabled: boolean;
    mode?: 'disabled' | 'static' | 'dynamic' | 'random';
    performance_priority?: 'lightweight' | 'balanced' | 'visual_first';
    preset_id?: number | null;
    curated_id?: number | null;
    upload_id?: string | null;
  };
  // 🆕 Прозрачность компонентов (0-1, null = использовать настройку шаблона или глобальную)
  componentOpacity?: number | null;
  // #VisualModernization: единый эффект фонов секционных компонентов для всей сборки.
  // none / soft-gradient / glass-lite / mesh-light / glow-accent
  componentVisualEffect?: ComponentVisualEffect;
}

export type ComponentVisualEffect =
  | 'none'
  | 'soft-gradient'
  | 'glass-lite'
  | 'mesh-light'
  | 'glow-accent';

export const COMPONENT_VISUAL_EFFECTS: ComponentVisualEffect[] = [
  'none',
  'soft-gradient',
  'glass-lite',
  'mesh-light',
  'glow-accent'
];

export interface GenerationPreview {
  template: {
    id: number;
    name: string;
    type: string;
    structure: unknown;
  };
  components: ComponentSelection[];
  theme: string;
  themeSource?: string;
  themeDetails?: ThemeResolutionInfo;
  contentTheme?: ContentTheme;
  siteDescriptionOverride?: string;
  templateAccess?: TemplateAccessInfo | null;
  availability?: TemplateAvailability | null;
  settings: {
    platform: string;
    geo: string;
    contentLanguage: string;
    advancedMode: boolean;
  };
  warnings?: TemplateWarning[];
  generatedAt: string;
}

export interface PreviewResponse {
  success: boolean;
  data: {
    preview: GenerationPreview;
    estimatedGenerationTime: number;
    totalComponents: number; // Уникальные типы компонентов
    totalInstances?: number; // 🆕 Общее количество экземпляров
    templateAccess?: TemplateAccessInfo;
    availability?: TemplateAvailability | null;
    warnings?: TemplateWarning[];
  };
}

export interface GenerationResult {
  id: number;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  templateUsed: number;
  componentsUsed: number;
  theme: string;
  platform: string;
  geo: string;
  contentLanguage: string;
  generationTime: number;
  downloadUrl?: string;
  previewUrl?: string;
  warnings?: TemplateWarning[];
  availability?: TemplateAvailability | null;
  error?: string;
}

export interface GenerationResponse {
  success: boolean;
  data: GenerationResult;
  error?: string;
}

// Состояние формы генератора V2
export interface GeneratorV2FormState {
  // Шаг 1: Основные настройки
  templateType: 'single' | 'multi-page';
  siteType: SiteType; // #Gen-Multi: Тип сайта (landing/multipage)
  htmlTemplate: number | null;
  theme: string;
  themeMode: ThemeMode;
  themeDescription: string;
  siteContentTheme: string;
  siteDescriptionOverride: string;
  
  // Шаг 2: Компоненты (опционально)
  useRecommendedComponents: boolean;
  selectedComponents: ComponentSelection[];
  // Состояние необязательных компонентов: 'random' (50% вероятность) / 'include' / 'exclude'
  optionalComponentsState: Record<string, 'random' | 'include' | 'exclude'>;
  // Состояние выбора компонентов: 'random' (случайный из категории) / конкретный variantCode
  componentVariantChoice: Record<string, 'random' | string>;
  
  // Шаг 3: Локализация
  platform: string; // #Core-Legal: Расширен до любой платформы из конфига
  geo: string; // #Core-Legal: Расширен до любой юрисдикции из конфига
  contentLanguage: string;
  legalDocsLanguage: string; // #Core-Legal: '' = язык сайта, 'en' = английский
  
  // #Core-Legal: Данные компании для юридических документов
  companyName: string;
  companyEmail: string;
  companyAddress: string;
  companyDomain: string;
  
  // Шаг 4: Расширенные настройки (опционально)
  advancedMode: boolean;
  communicationStyle: 'professional' | 'friendly' | 'aggressive' | 'minimal' | 'creative';
  specialRequirements: string;
  debugMode: boolean;
  
  // 🆕 #F-Background: Настройки глобального фона
  backgroundEnabled: boolean;
  backgroundMode: 'disabled' | 'static' | 'dynamic' | 'random';
  backgroundPerformance: 'lightweight' | 'balanced' | 'visual_first';
  backgroundPresetId: number | null;
  backgroundCuratedId: number | null;
  backgroundUploadId: string;
  
  // 🆕 Прозрачность компонентов (null = использовать настройку шаблона или глобальную)
  componentOpacity: number | null;

  // #VisualModernization: component visual effect preset
  componentVisualEffect: ComponentVisualEffect;
}

// Состояние загрузки и ошибок
export interface GeneratorV2State {
  // Загрузка данных
  loading: boolean;
  error: string;
  
  // Данные
  htmlTemplates: HtmlTemplate[];
  availableComponents: ComponentVariant[];
  componentsByType: ComponentsByType;
  themes: CssTheme[];
  contentThemes: ContentTheme[];
  
  // Текущий шаблон и его компоненты
  selectedTemplate: HtmlTemplate | null;
  recommendedComponents: RecommendedComponent[];
  
  // Предварительный просмотр
  preview: GenerationPreview | null;
  previewLoading: boolean;
  previewTotalComponents: number; // 🆕 Количество уникальных типов компонентов
  previewTotalInstances: number; // 🆕 Общее количество экземпляров компонентов
  previewWarnings: TemplateWarning[];
  templateAccess: TemplateAccessInfo | null;
  
  // Генерация
  generationInProgress: boolean;
  generationResult: GenerationResult | null;
}

// Шаги формы
export type GeneratorStep = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface StepConfig {
  title: string;
  description: string;
  isOptional?: boolean;
  canSkip?: boolean;
}

export const GENERATOR_STEPS: Record<GeneratorStep, StepConfig> = {
  1: {
    title: 'Шаблон',
    description: 'Выберите тип сайта и HTML шаблон'
  },
  2: {
    title: 'Оформление',
    description: 'Выберите режим подбора темы и (при необходимости) тему оформления'
  },
  3: {
    title: 'Контент',
    description: 'Выберите тематику контента и (опционально) расширенные настройки'
  },
  4: {
    title: 'Блоки',
    description: 'Настройте компоненты (для опытных пользователей)',
    isOptional: true,
    canSkip: true
  },
  5: {
    title: 'Язык и регион',
    description: 'Платформа, юрисдикция и языки'
  },
  6: {
    title: 'Фон',
    description: 'Глобальный фон (под элементами)'
  },
  7: {
    title: 'Проверка',
    description: 'Проверка и запуск генерации'
  }
};













export interface TemplateComponents {
  required: string[];
  optional: string[];
  total: number;
}
